import React from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { useSiteMetadata } from '../hooks/useSiteMetadata'
import { useMergePrismicPreviewData } from 'gatsby-plugin-prismic-previews'

export const Seo = ({ children, datePublished, dateModified, isArticle, pageData, pathname }) => {
  const staticData = useStaticQuery(graphql`
    {
      prismicConfig {
        data {
          company_address {
            richText
            text
          }
          company_display_name
          company_logo {
            url(imgixParams: { w: 100 })
          }
          company_phone
          facebook_image {
            url
          }
          social {
            social_network_url {
              url
            }
          }
        }
      }
      googlePlacesPlace {
        rating
        user_ratings_total
      }
      allGooglePlacesReview {
        edges {
          node {
            author_name
            author_url
            profile_photo_url
            rating
            text
            time
          }
        }
      }
      allPrismicPage {
        edges {
          node {
            data {
              body {
                ... on PrismicPageDataBodyFoodMenu {
                  slice_type
                  primary {
                    menu {
                      document {
                        ... on PrismicMenu {
                          _previewable
                          data {
                            title {
                              text
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            url
            uid
          }
        }
      }
    }
  `)

  // Hook to fetch Backup MetaData from Gatsby
  const { companyName, description, logo, twitterUsername, siteUrl } = useSiteMetadata()

  // Declare all data points
  const data = useMergePrismicPreviewData(staticData)
  const configData = data.prismicConfig.data
  const authorData = pageData?.author?.[0]
  const foodMenus = data.allPrismicPage.edges
    .filter((item) => item.node.data.body.some((slice) => slice.slice_type === 'food_menu'))
    .map((item) => {
      const foodMenuSlice = item.node.data.body.find((slice) => slice.slice_type === 'food_menu')
      const menuTitle = foodMenuSlice?.primary?.menu?.document?.data?.title?.text || 'No Title'
      return {
        url: `${siteUrl}${item.node.url}`,
        title: menuTitle
      }
    })
  const reviews = data.allGooglePlacesReview.edges.map((review) => ({
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.node.author_name,
      url: review.node.author_url,
      image: review.node.profile_photo_url
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.node.rating.toString()
    },
    datePublished: new Date(review.node.time * 1000).toISOString().split('T')[0],
    reviewBody: review.node.text
  }))

  const url = `${siteUrl}${pathname || ''}`
  const { page_image, page_title, meta_description, meta_title } = pageData
  const { company_display_name, company_logo, company_phone, facebook_image } = configData
  const title = meta_title || page_title?.text
  const name = company_display_name || companyName
  const pageDescription = meta_description || description

  // Generate OG Image
  let image = facebook_image.url
  if (page_image?.url) {
    image = `${page_image.url?.split('?')[0]}?format&q=&auto=compress&w=1200&h=630&fit=crop&txt=${title
      ?.split(' ')
      .join(
        '+'
      )}&txt-shad=5&txt-pad=30&txt-color=FFFFFF&txt-align=bottom,center&txt-size=100&txt-fit=max&txt-font=Helvetica,Bold&border=5,000000&pad=10&mark-pad=30&mark-align=top,center&mark=${company_logo.url?.split('?')[0]}`
  }

  // Create Social Media Array
  const socialMedia = configData.social.map((obj) => obj['social_network_url'].url)

  const baseSchema = [
    {
      '@context': 'https://schema.org/',
      '@type': 'WebSite',
      url,
      name: name,
      description: description,
      sameAs: socialMedia
    },
    {
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      url,
      name: name,
      sameAs: socialMedia,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'UK',
        addressLocality: 'Sandgate',
        addressRegion: 'Kent',
        postalCode: 'CT20 3BD',
        streetAddress: '7 Sandgate High Street'
      },
      image: company_logo.url || logo,
      logo: {
        '@type': 'ImageObject',
        url: company_logo.url || logo
      },
      telephone: company_phone
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
      name: 'The Royal Norfolk Hotel',
      url: 'https://www.royalnorfolkhotel.com',
      logo: company_logo.url || logo,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: company_phone,
        contactType: 'Reservations'
      },
      telephone: company_phone,
      checkinTime: '15:00:00',
      checkoutTime: '10:00:00',
      numberOfRooms: 14,
      petsAllowed: false,
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'UK',
        addressLocality: 'Sandgate',
        addressRegion: 'Kent',
        postalCode: 'CT20 3BD',
        streetAddress: '7 Sandgate High Street'
      },
      starRating: {
        '@type': 'Rating',
        ratingValue: '3'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: data.googlePlacesPlace.rating,
        reviewCount: data.googlePlacesPlace.user_ratings_total
      },
      review: reviews,
      priceRange: '££',
      sameAs: socialMedia,
      subOrganization: [
        {
          '@type': 'Restaurant',
          name: 'The Royal Norfolk Hotel Restaurant',
          url: 'https://www.royalnorfolkhotel.com/restaurant/',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'UK',
            addressLocality: 'Sandgate',
            addressRegion: 'Kent',
            postalCode: 'CT20 3BD',
            streetAddress: '7 Sandgate High Street'
          },
          telephone: company_phone,
          servesCuisine: 'British',
          priceRange: '££',
          hasMenu: foodMenus.map((menu) => ({
            '@type': 'Menu',
            name: menu.title,
            url: menu.url
          }))
        },
        {
          '@type': 'BarOrPub',
          name: 'The Royal Norfolk Hotel Bar',
          url: 'https://www.royalnorfolkhotel.com/bar/',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'UK',
            addressLocality: 'Sandgate',
            addressRegion: 'Kent',
            postalCode: 'CT20 3BD',
            streetAddress: '7 Sandgate High Street'
          },
          telephone: company_phone,
          servesCuisine: 'British',
          priceRange: '££'
        }
      ]
    }
  ]

  const schema = isArticle
    ? [
        ...baseSchema,
        {
          '@context': 'https://schema.org/',
          '@type': ['BlogPosting', 'NewsArticle', 'Article'],
          url,
          name: name,
          headline: title,
          image: [page_image?.url || facebook_image.url],
          description: description,
          author: {
            '@type': 'Person',
            name: authorData?.name || twitterUsername,
            url: `${siteUrl}${authorData?.url}`
          },
          publisher: {
            '@type': 'Organization',
            url,
            logo: {
              '@type': 'ImageObject',
              url: authorData?.image.url
            },
            name: authorData?.name || twitterUsername
          },
          datePublished,
          dateModified
        }
      ]
    : baseSchema

  return (
    <React.Fragment key='seo'>
      <title>{title}</title>
      <meta name='author' content={authorData?.name || twitterUsername} />
      <link rel='canonical' href={url} data-baseprotocol='https:' data-basehost={siteUrl} />
      <meta name='description' content={pageDescription} />
      <meta name='image' content={image} />
      <meta property='og:title' content={title} />
      <meta property='og:description' content={pageDescription} />
      <meta property='og:image' content={image} />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      <meta property='og:type' content={isArticle ? 'article' : 'website'} />
      <meta property='og:url' content={url} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={title} />
      <meta property='twitter:url' content={url} />
      <meta property='twitter:domain' content={new URL(siteUrl).hostname} />
      <meta name='twitter:description' content={pageDescription} />
      <meta name='twitter:image' content={image} />
      <meta name='twitter:creator' content={twitterUsername} />
      <script type='application/ld+json'>{JSON.stringify(schema)}</script>
      {children}
    </React.Fragment>
  )
}

export default Seo
